import type { ClassReference } from "../model/types.js";
import type { DecompiledSource } from "./SourcePrinter.js";
import type {
  DecompileProgress,
  DecompilerWorkerRequest,
  DecompilerWorkerResponse,
  SerializedError,
} from "./DecompilerProtocol.js";

export type { DecompileProgress } from "./DecompilerProtocol.js";

type DecompilerControlRequest =
  | {
      readonly type: "configure";
      readonly standardLibraryBaseUrl: string;
    }
  | {
      readonly type: "register-artifact";
      readonly artifactId: string;
      readonly name: string;
      readonly blob: Blob;
    }
  | {
      readonly type: "unregister-artifact";
      readonly artifactId: string;
    }
  | {
      readonly type: "clear-artifacts";
    };

interface PendingRequest {
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
  readonly onProgress?: (progress: DecompileProgress) => void;
}

function deserializeError(serialized: SerializedError): Error {
  const error = new Error(serialized.message);
  error.name = serialized.name;
  if (serialized.stack !== null) {
    error.stack = serialized.stack;
  }
  return error;
}

/** Main-thread proxy for the decompiler worker. */
export class DecompilerService {
  private readonly worker: Worker;
  private readonly pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 1;
  private workerFailure: Error | null = null;

  public constructor(standardLibraryBaseUrl: URL) {
    this.worker = new Worker(
      new URL("./DecompilerWorker.ts", import.meta.url),
      {
        name: "jd-core-decompiler",
        type: "module",
      },
    );
    this.worker.onmessage = (
      event: MessageEvent<DecompilerWorkerResponse>,
    ): void => {
      this.handleResponse(event.data);
    };
    this.worker.onerror = (event: ErrorEvent): void => {
      this.failWorker(
        new Error(event.message || "The decompiler worker failed"),
      );
    };
    this.worker.onmessageerror = (): void => {
      this.failWorker(
        new Error("The decompiler worker returned an unreadable message"),
      );
    };

    void this.sendAcknowledgedRequest({
      type: "configure",
      standardLibraryBaseUrl: standardLibraryBaseUrl.href,
    }).then(
      () => undefined,
      (error: unknown) => this.failWorker(error),
    );
  }

  public registerArtifact(artifactId: string, file: File): Promise<void> {
    // Send the original immutable File rather than the main-thread Uint8Array.
    // This avoids structured-cloning a large archive buffer into the worker;
    // the worker materializes and caches its own view only when first needed.
    return this.sendAcknowledgedRequest({
      type: "register-artifact",
      artifactId,
      name: file.name,
      blob: file,
    });
  }

  public unregisterArtifact(artifactId: string): Promise<void> {
    return this.sendAcknowledgedRequest({
      type: "unregister-artifact",
      artifactId,
    });
  }

  public clearArtifacts(): Promise<void> {
    return this.sendAcknowledgedRequest({ type: "clear-artifacts" });
  }

  public decompile(
    reference: ClassReference,
    onProgress?: (progress: DecompileProgress) => void,
  ): Promise<DecompiledSource> {
    const requestId = this.allocateRequestId();
    const request: DecompilerWorkerRequest = {
      type: "decompile",
      requestId,
      reference: {
        artifactId: reference.artifactId,
        entryPath: reference.entryPath,
        internalName: reference.internalName,
        displayName: reference.displayName,
      },
    };

    return new Promise<DecompiledSource>((resolve, reject) => {
      this.pendingRequests.set(requestId, {
        resolve: (value) => resolve(value as DecompiledSource),
        reject,
        ...(onProgress === undefined ? {} : { onProgress }),
      });
      this.postRequest(request);
    });
  }

  private sendAcknowledgedRequest(
    request: DecompilerControlRequest,
  ): Promise<void> {
    const requestId = this.allocateRequestId();
    const fullRequest = { ...request, requestId } as DecompilerWorkerRequest;

    return new Promise<void>((resolve, reject) => {
      this.pendingRequests.set(requestId, {
        resolve: () => resolve(),
        reject,
      });
      this.postRequest(fullRequest);
    });
  }

  private allocateRequestId(): number {
    return this.nextRequestId++;
  }

  private postRequest(request: DecompilerWorkerRequest): void {
    if (this.workerFailure !== null) {
      const pending = this.pendingRequests.get(request.requestId);
      this.pendingRequests.delete(request.requestId);
      pending?.reject(this.workerFailure);
      return;
    }
    this.worker.postMessage(request);
  }

  private handleResponse(response: DecompilerWorkerResponse): void {
    const pending = this.pendingRequests.get(response.requestId);
    if (pending === undefined) {
      return;
    }

    switch (response.type) {
      case "progress":
        pending.onProgress?.(response.progress);
        return;
      case "acknowledged":
        this.pendingRequests.delete(response.requestId);
        pending.resolve(undefined);
        return;
      case "decompiled":
        this.pendingRequests.delete(response.requestId);
        pending.resolve({
          source: response.source,
          locations: response.locations,
          folds: response.folds,
        } satisfies DecompiledSource);
        return;
      case "failed":
        this.pendingRequests.delete(response.requestId);
        pending.reject(deserializeError(response.error));
    }
  }

  private failWorker(reason: unknown): void {
    if (this.workerFailure !== null) {
      return;
    }
    this.workerFailure =
      reason instanceof Error ? reason : new Error(String(reason));
    for (const pending of this.pendingRequests.values()) {
      pending.reject(this.workerFailure);
    }
    this.pendingRequests.clear();
  }
}
